/// useDebugSession: React hook for debug session lifecycle management.
/// Provides start/stop/step/continue/breakpoint operations against
/// the BPMN engine worker via WorkerManager.
///
/// Phase 20, Plan 03.

import { useCallback } from "react";
import { getWorkerManager } from "@/lib/WorkerManager";
import { useDebugStore } from "@/stores/debugStore";
import type { DebugMode } from "@/types/debug";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Timeout for debug commands (may take longer than normal operations). */
const DEBUG_COMMAND_TIMEOUT_MS = 15000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for managing the debug session lifecycle.
 * Sends debug commands to the BPMN engine worker and updates debugStore state.
 *
 * @returns Debug session actions and current mode.
 */
export function useDebugSession() {
  const mode = useDebugStore((s) => s.mode);

  /**
   * Start a debug session for the given instance.
   * Collects current breakpoints from debugStore and sends startDebug command.
   */
  const startDebug = useCallback(async (instanceKey: string) => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) {
      console.warn("[useDebugSession] No BPMN engine worker is running");
      return;
    }

    // Collect breakpoints from debugStore
    const store = useDebugStore.getState();
    const breakpointsArray = Array.from(store.breakpoints.values()).map((bp) => ({
      element_id: bp.bpmnElementId,
      condition: bp.condition,
    }));

    try {
      await manager.sendCommand(
        engineWorkerId,
        {
          type: "startDebug" as const,
          direction: "cmd" as const,
          payload: {
            instance_key: instanceKey,
            breakpoints: JSON.stringify(breakpointsArray),
          },
        } as never,
        DEBUG_COMMAND_TIMEOUT_MS,
      );

      // Update store state on success
      store.startDebug(instanceKey);
    } catch (err) {
      console.error("[useDebugSession] startDebug failed:", err);
    }
  }, []);

  /**
   * Stop the current debug session.
   * Sends stopDebug command and clears visual overlays in the editor worker.
   */
  const stopDebug = useCallback(async () => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) return;

    try {
      await manager.sendCommand(
        engineWorkerId,
        {
          type: "stopDebug" as const,
          direction: "cmd" as const,
          payload: {},
        } as never,
        DEBUG_COMMAND_TIMEOUT_MS,
      );

      // Clear visual overlays in editor worker
      const editorWorkerId = manager.getWorkerIdByRole("bpmn-editor");
      if (editorWorkerId) {
        manager
          .sendCommand(
            editorWorkerId,
            {
              type: "debugOverlay" as const,
              direction: "cmd" as const,
              subCommand: "clearAll",
              payload: {},
            } as never,
            5000,
          )
          .catch(() => {
            // Best-effort: editor may not support this command yet
          });
      }

      useDebugStore.getState().stopDebug();
    } catch (err) {
      console.error("[useDebugSession] stopDebug failed:", err);
    }
  }, []);

  /**
   * Step execution forward by one element.
   */
  const step = useCallback(async () => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) return;

    const store = useDebugStore.getState();
    const instanceKey = store.currentInstanceKey;
    if (!instanceKey) return;

    store.setMode("stepping");

    try {
      await manager.sendCommand(
        engineWorkerId,
        {
          type: "stepExecution" as const,
          direction: "cmd" as const,
          payload: { instance_key: instanceKey },
        } as never,
        DEBUG_COMMAND_TIMEOUT_MS,
      );
    } catch (err) {
      console.error("[useDebugSession] step failed:", err);
      store.setMode("paused");
    }
  }, []);

  /**
   * Continue execution until the next breakpoint or completion.
   */
  const continueExecution = useCallback(async () => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) return;

    const store = useDebugStore.getState();
    const instanceKey = store.currentInstanceKey;
    if (!instanceKey) return;

    store.setMode("running");

    try {
      await manager.sendCommand(
        engineWorkerId,
        {
          type: "continueExecution" as const,
          direction: "cmd" as const,
          payload: { instance_key: instanceKey },
        } as never,
        DEBUG_COMMAND_TIMEOUT_MS,
      );
    } catch (err) {
      console.error("[useDebugSession] continueExecution failed:", err);
      store.setMode("paused");
    }
  }, []);

  /**
   * Toggle a breakpoint on/off for a given element.
   * Sends the appropriate command to the engine worker and updates
   * the editor worker to show/hide the breakpoint indicator.
   */
  const toggleBreakpoint = useCallback(async (elementId: string, bpmnElementId: string) => {
    const manager = getWorkerManager();
    const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
    if (!engineWorkerId) return;

    const store = useDebugStore.getState();
    const exists = store.breakpoints.has(elementId);

    try {
      if (exists) {
        await manager.sendCommand(
          engineWorkerId,
          {
            type: "removeBreakpoint" as const,
            direction: "cmd" as const,
            payload: { element_id: bpmnElementId },
          } as never,
          DEBUG_COMMAND_TIMEOUT_MS,
        );
      } else {
        await manager.sendCommand(
          engineWorkerId,
          {
            type: "setBreakpoint" as const,
            direction: "cmd" as const,
            payload: { element_id: bpmnElementId },
          } as never,
          DEBUG_COMMAND_TIMEOUT_MS,
        );
      }

      // Update local store
      store.toggleBreakpoint(elementId, bpmnElementId);

      // Update editor worker visual indicator
      const editorWorkerId = manager.getWorkerIdByRole("bpmn-editor");
      if (editorWorkerId) {
        manager
          .sendCommand(
            editorWorkerId,
            {
              type: "debugOverlay" as const,
              direction: "cmd" as const,
              subCommand: exists ? "removeBreakpoint" : "addBreakpoint",
              payload: { elementId: elementId },
            } as never,
            5000,
          )
          .catch(() => {
            // Best-effort: editor may not support this command yet
          });
      }
    } catch (err) {
      console.error("[useDebugSession] toggleBreakpoint failed:", err);
    }
  }, []);

  /**
   * Set a conditional breakpoint on a given element.
   */
  const setConditionalBreakpoint = useCallback(
    async (elementId: string, bpmnElementId: string, expression: string) => {
      const manager = getWorkerManager();
      const engineWorkerId = manager.getWorkerIdByRole("bpmn-engine");
      if (!engineWorkerId) return;

      try {
        await manager.sendCommand(
          engineWorkerId,
          {
            type: "setBreakpoint" as const,
            direction: "cmd" as const,
            payload: {
              element_id: bpmnElementId,
              condition: expression,
            },
          } as never,
          DEBUG_COMMAND_TIMEOUT_MS,
        );

        // Update local store
        useDebugStore.getState().setConditionalBreakpoint(elementId, bpmnElementId, expression);

        // Show breakpoint indicator in editor
        const editorWorkerId = manager.getWorkerIdByRole("bpmn-editor");
        if (editorWorkerId) {
          manager
            .sendCommand(
              editorWorkerId,
              {
                type: "debugOverlay" as const,
                direction: "cmd" as const,
                subCommand: "addBreakpoint",
                payload: { elementId: elementId },
              } as never,
              5000,
            )
            .catch(() => {
              // Best-effort
            });
        }
      } catch (err) {
        console.error("[useDebugSession] setConditionalBreakpoint failed:", err);
      }
    },
    [],
  );

  return {
    startDebug,
    stopDebug,
    step,
    continueExecution,
    toggleBreakpoint,
    setConditionalBreakpoint,
    mode: mode as DebugMode,
  };
}
